import { useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ActionSlot } from "./chrome";
import { cn } from "@/lib/cn";
import { AddCircleIcon, CameraIcon, CloseIcon, LockIcon, MinusIcon } from "@/components/icons";
import { usePreviewMode } from "./preview/mode";

/*
 * Pieces every staff screen is built from. Phone first: every target is at
 * least 44px, the main rows and buttons 52–66px, presses show as
 * --surface-sunk (no hover), and text inputs are 16px so iOS doesn't zoom.
 */

/**
 * The commit bar at the foot of a commit screen. The primary is disabled
 * until the form is valid, and its label says what's missing ("12 left").
 * In the Plan's phone view nothing commits: it reads "Preview only".
 */
export function ActionBar({
  primary,
  onPrimary,
  disabled,
  busy,
  secondary,
  onSecondary,
  tone = "deodar",
}: {
  primary: string;
  onPrimary: () => void;
  disabled?: boolean;
  busy?: boolean;
  secondary?: string;
  onSecondary?: () => void;
  tone?: "deodar" | "brass";
}) {
  const preview = usePreviewMode();
  const slot = useContext(ActionSlot);
  if (!slot) return null;
  return createPortal(
    <div className="flex gap-2.5 border-t border-line-strong bg-surface px-4 pt-2.5 pb-[calc(14px+env(safe-area-inset-bottom))]">
      <div className="mx-auto flex w-full max-w-[720px] gap-2.5">
        {secondary && (
          <button type="button" onClick={onSecondary} disabled={preview} className="min-h-[52px] shrink-0 rounded-md border border-line-strong bg-sunk px-4 text-[15px] font-semibold active:bg-canvas disabled:opacity-[.42]">
            {secondary}
          </button>
        )}
        <button
          type="button"
          onClick={onPrimary}
          disabled={disabled || busy || preview}
          className={cn(
            "kadil min-h-[52px] flex-1 rounded-md px-4 text-[16px] font-bold transition-[filter] active:brightness-95 disabled:opacity-[.42]",
            tone === "brass" ? "bg-brass text-brass-ink" : "bg-deodar text-on-fill",
          )}
        >
          {preview ? "Preview only" : busy ? "Saving…" : primary}
        </button>
      </div>
    </div>,
    slot,
  );
}

/* ───────────────────────────── Surfaces ───────────────────────────── */

export function Card({ children, className, strong }: { children: ReactNode; className?: string; strong?: boolean }) {
  return <div className={cn("rounded-lg bg-surface", strong ? "border-2 border-deodar" : "border border-line", className)}>{children}</div>;
}

export function Kicker({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("font-mono text-[10.5px] uppercase tracking-[.12em] text-subtle", className)}>{children}</div>;
}

/** "TO DO · 2" */
export function SectionHead({ label, count, className }: { label: string; count?: number; className?: string }) {
  return (
    <Kicker className={cn("mt-2 px-1", className)}>
      {label}
      {count !== undefined && ` · ${count}`}
    </Kicker>
  );
}

export function Avatar({ initials, size = 36 }: { initials: string; size?: 36 | 64 }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full border border-line-strong bg-deodar-tint font-mono font-semibold text-deodar",
        size === 36 ? "size-9 text-[13px]" : "size-16 text-[22px]",
      )}
    >
      {initials}
    </span>
  );
}

/** The 18px rhodo count on a tab or a row. */
export function Badge({ n, className }: { n: number; className?: string }) {
  if (n <= 0) return null;
  return <span className={cn("inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rhodo px-1 font-mono text-[10.5px] font-semibold text-[#FFFDF8]", className)}>{n}</span>;
}

export function Chip({ tone, children }: { tone: "brass" | "rhodo" | "pine" | "slate" | "deodar"; children: ReactNode }) {
  const c = { brass: "var(--brass)", rhodo: "var(--rhododendron)", pine: "var(--pine)", slate: "var(--slate)", deodar: "var(--deodar)" }[tone];
  return (
    <span
      className="inline-flex h-6 shrink-0 items-center rounded-full px-2.5 text-[12.5px] font-semibold whitespace-nowrap"
      style={{ background: `color-mix(in srgb, ${c} 14%, var(--surface))`, color: `color-mix(in srgb, ${c} 80%, var(--ink))` }}
    >
      {children}
    </span>
  );
}

export const Arrival = ({ time }: { time: string }) => <Chip tone="brass">Guest arrives {time}</Chip>;
export const Late = () => <Chip tone="rhodo">Late</Chip>;

/** 8px progress bar. */
export function Progress({ value, total, tone = "pine", className }: { value: number; total: number; tone?: "pine" | "brass" | "deodar"; className?: string }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div className={cn("h-2 overflow-hidden rounded-full bg-sunk", className)} role="progressbar" aria-valuenow={value} aria-valuemax={total}>
      <div className={cn("h-full rounded-full transition-[width] duration-300", tone === "pine" ? "bg-pine" : tone === "brass" ? "bg-brass" : "bg-deodar")} style={{ width: `${pct}%` }} />
    </div>
  );
}

/** The big primary inside a card (Start / Continue): 52px, kadil. */
export function BigButton({ children, onClick, tone = "deodar", className, disabled }: { children: ReactNode; onClick: () => void; tone?: "deodar" | "tint"; className?: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex min-h-[52px] w-full items-center justify-center gap-2 rounded-md px-4 text-[16px] font-bold active:brightness-95 disabled:opacity-[.42]",
        tone === "deodar" ? "kadil bg-deodar text-on-fill" : "border border-deodar bg-deodar-tint text-ink active:bg-sunk",
        className,
      )}
    >
      {children}
    </button>
  );
}

/** A pressable row in a list card: 66px, a 42px icon tile, label, sub and a count. */
export function NavRow({ icon, label, sub, count, onClick }: { icon: ReactNode; label: string; sub?: string | null; count?: number; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex min-h-[66px] w-full items-center gap-3 px-3.5 text-left active:bg-sunk [&+&]:border-t [&+&]:border-line">
      <span className="flex size-[42px] shrink-0 items-center justify-center rounded-md bg-deodar-tint text-deodar">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15.5px] font-semibold">{label}</span>
        {sub && <span className="block truncate text-[13px] text-muted">{sub}</span>}
      </span>
      {count !== undefined && <Badge n={count} />}
    </button>
  );
}

/** The 46px − / + stepper. `warn` shows the number in rhodo (fewer back than sent). */
export function BigStepper({ value, onChange, min = 0, max = 999, label, warn }: { value: number; onChange: (n: number) => void; min?: number; max?: number; label: string; warn?: boolean }) {
  const btn = "flex size-[46px] items-center justify-center text-muted active:bg-sunk disabled:opacity-35";
  return (
    <span role="group" aria-label={label} className="inline-flex shrink-0 items-center rounded-md border border-line-strong bg-surface">
      <button type="button" className={btn} aria-label={`Fewer ${label.toLowerCase()}`} disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))}>
        <MinusIcon size={22} />
      </button>
      <span className={cn("min-w-10 text-center font-mono text-[17px] font-semibold tabular-nums", warn && "text-rhodo")} aria-live="polite">
        {value}
      </span>
      <button type="button" className={btn} aria-label={`More ${label.toLowerCase()}`} disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))}>
        <AddCircleIcon size={22} />
      </button>
    </span>
  );
}

/** One-of chips with a 44px target: where a thing is, what's wrong, why a code changed. */
export function Chips<T extends string>({ options, value, onChange, label, grid }: { options: { value: T; label: string }[]; value: T | null; onChange: (v: T) => void; label: string; grid?: boolean }) {
  return (
    <div role="radiogroup" aria-label={label} className={cn(grid ? "grid grid-cols-2 gap-2" : "flex flex-wrap gap-2")}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex min-h-11 items-center justify-center rounded-full border px-4 text-[15px]",
              on ? "border-deodar bg-deodar-tint font-semibold text-ink shadow-[inset_0_0_0_1px_var(--deodar)]" : "border-line-strong bg-surface text-muted active:bg-sunk",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** A field's label over its control. */
export function FieldBlock({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div>
        <div className="text-[14.5px] font-semibold">{label}</div>
        {hint && <div className="text-[13px] text-muted">{hint}</div>}
      </div>
      {children}
    </div>
  );
}

/** A 16px text input (no zoom on iOS). */
export function TextBox(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn("min-h-[52px] w-full rounded-md border border-line-strong bg-surface px-3.5 text-[16px] outline-none focus:border-deodar focus:shadow-[0_0_0_3px_var(--deodar-tint)]", props.className)}
    />
  );
}

/**
 * Take or pick a photo. The file input is hidden behind a real button; the
 * chosen photo shows as a thumbnail with a way to drop it.
 */
export function PhotoPick({ file, onChange, label = "Add a photo" }: { file: File | null; onChange: (f: File | null) => void; label?: string }) {
  const input = useRef<HTMLInputElement>(null);
  const url = useObjectUrl(file);
  return (
    <div>
      <input
        ref={input}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          onChange(e.target.files?.[0] ?? null);
          e.target.value = "";
        }}
      />
      {file && url ? (
        <div className="relative inline-block">
          <img src={url} alt="" className="h-28 w-28 rounded-md border border-line object-cover" />
          <button type="button" aria-label="Remove the photo" onClick={() => onChange(null)} className="absolute -top-2 -right-2 flex size-8 items-center justify-center rounded-full border border-line-strong bg-surface">
            <CloseIcon size={16} />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => input.current?.click()} className="flex min-h-[52px] items-center gap-2 rounded-md border border-dashed border-line-strong bg-sunk px-4 text-[15px] font-semibold text-muted active:bg-canvas">
          <CameraIcon size={20} />
          {label}
        </button>
      )}
    </div>
  );
}

function useObjectUrl(file: File | null): string | null {
  const url = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => void (url && URL.revokeObjectURL(url)), [url]);
  return url;
}

/** "You can't open this any more. Ask your manager." The tab bar stays. */
export function Blocked() {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      <span className="flex size-14 items-center justify-center rounded-lg bg-sunk text-muted">
        <LockIcon size={28} />
      </span>
      <div className="font-display text-[20px] font-semibold">You can&rsquo;t open this any more.</div>
      <div className="text-[15px] text-muted">Ask your manager.</div>
    </div>
  );
}

export function Loading() {
  return (
    <div className="flex flex-col gap-3" aria-busy>
      <div className="shimmer h-24 rounded-lg" />
      <div className="shimmer h-16 rounded-lg" />
      <div className="shimmer h-16 rounded-lg" />
    </div>
  );
}

export function Failed({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <div className="text-[15px] text-muted">{message}</div>
      <button type="button" onClick={onRetry} className="min-h-11 rounded-md border border-line-strong bg-sunk px-4 font-semibold active:bg-canvas">
        Try again
      </button>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="rounded-lg border border-dashed border-line-strong px-4 py-6 text-center text-[15px] text-muted">{children}</div>;
}
